import * as React from 'react';
import { Avatar, Box, CircularProgress, IconButton, Menu, MenuItem, TextField, Typography, styled } from '@mui/material';
import { Close, ImageOutlined, MoreVert, Send } from '@mui/icons-material';
import useWindowDimensions from '../../util/isMobile';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createComment, getComments } from '../../controllers/posts/comments';
import { getSinglePost, uploadMedia } from '../../controllers/posts/posts';
import { UserContext } from '../../context/UserContext';
import { getConversation, markConversationRead, touchConversation } from '../../controllers/conversation/conversation';
import { getAccount } from '../../controllers/accounts/accounts';
import { DeleteConversation } from './ConversationActions';
import { useNavigate, useParams } from 'react-router';
import { stripMessageContent } from '../../util/util';

const NAV = '#202c41';
const BG = '#F2F4F8';
const MUTED = '#8A95A3';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

export const MessageItem = ({ item, last_post }) => {
  const myRef = React.useRef(null);
  const navigate = useNavigate();
  const { userDetails } = React.useContext(UserContext);
  React.useEffect(() => {
    if (myRef.current) {
      myRef.current.scrollIntoView();
    }
  });
  const isMe = item?.account?.id === userDetails?.id;
  const text = stripMessageContent(item?.content);
  const media = item?.media_attachments ?? [];

  return (
    <Box
      ref={last_post ? myRef : null}
      sx={{
        display: 'flex',
        flexDirection: isMe ? 'row-reverse' : 'row',
        alignItems: 'flex-end',
        gap: 1,
        width: '100%',
      }}
    >
      <Avatar
        alt={item?.account?.username}
        src={item?.account?.avatar}
        onClick={() => item?.account?.id && navigate('/profile/' + item.account.id)}
        sx={{ width: 32, height: 32, cursor: item?.account?.id ? 'pointer' : 'default', flexShrink: 0 }}
      />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, maxWidth: '70%', alignItems: isMe ? 'flex-end' : 'flex-start' }}>
        {media.map((m, i) => (
          <Box
            key={i}
            component="img"
            src={m.preview_url || m.url}
            sx={{ width: 220, maxWidth: '100%', borderRadius: 3, display: 'block' }}
          />
        ))}
        {!!text && (
          <Box sx={{
            background: isMe ? NAV : '#fff',
            color: isMe ? '#fff' : NAV,
            borderRadius: isMe ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
            px: 2,
            py: 1.25,
            boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
            wordBreak: 'break-word',
          }}>
            <Typography fontSize={14} lineHeight={1.5}>{text}</Typography>
          </Box>
        )}
        <Typography fontSize={11} sx={{ color: MUTED, px: 0.5 }}>
          {new Date(item?.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </Typography>
      </Box>
    </Box>
  );
};

export function SingleConversation({ init_id }) {
    const { width } = useWindowDimensions()
    const navigate = useNavigate()
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [content, setContent] = React.useState("")
    const [pendingMedia, setPendingMedia] = React.useState([])
    const [uploadingMedia, setUploadingMedia] = React.useState(false)
    const open = Boolean(anchorEl);
    const { userDetails } = React.useContext(UserContext)
    let token = userDetails?.token ?? ''

    // The conversation list only gives us last_status, which drifts every time
    // someone replies. Walk its ancestors to find the true first message of the
    // thread, then treat that as the anchor for both display and replies, so
    // every reply attaches to the same root instead of whatever happened to be
    // "last" when this page loaded.
    const { data: initContext } = useQuery({ queryKey: ['getComments', init_id, token], queryFn: () => getComments(init_id, token) })
    const root_id = initContext?.ancestors?.[0]?.id ?? init_id

    const { data } = useQuery({ queryKey: ['getComments', root_id, token], queryFn: () => getComments(root_id, token) })
    let conversation = data ?? []
    const { data:post } = useQuery({ queryKey: ['getSinglePost', root_id, token], queryFn: () => getSinglePost(root_id, token) })
    const root_post = React.useMemo(() => post ?? {}, [post])
    const { conversation_id } = useParams()
    const queryClient = useQueryClient()

    // Viewing the thread is what "reads" it — mark it read on open (and
    // whenever we land on it again), independent of whether the ancestor
    // walk/root queries above have resolved yet. Invalidate the badge/list
    // query afterward so the unread count updates immediately instead of
    // waiting for that component's own polling/staleness.
    React.useEffect(() => {
        if (!conversation_id || !token || !userDetails?.id) return
        markConversationRead({ token, id: conversation_id, user: userDetails.id })
            .then(() => queryClient.invalidateQueries({ queryKey: ['getMyConversations'] }))
    }, [conversation_id, token, userDetails?.id])

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

  // Deriving "the other person" from thread content breaks the moment there's
  // no reply yet (a brand-new conversation is 100% posts authored by me) —
  // our own conversation record knows the participants regardless of thread
  // state, so use that as the primary source and only fall back to scanning
  // the thread if that lookup isn't available for some reason.
  const { data: conversationDoc } = useQuery({
    queryKey: ['getConversation', conversation_id, token],
    queryFn: () => getConversation(token, conversation_id),
    enabled: !!conversation_id && !!token,
  })
  const otherId = conversationDoc?.conversation?.participants?.find((p) => p !== userDetails?.id)
  const { data: otherAccountFromParticipants } = useQuery({
    queryKey: ['getAccount', otherId, token],
    queryFn: () => getAccount(otherId, token),
    enabled: !!otherId && !!token,
  })

  const otherAccount = React.useMemo(() => {
    if (otherAccountFromParticipants?.id) return otherAccountFromParticipants
    const posts = [root_post, ...(conversation?.descendants ?? [])]
    return posts.find((p) => p?.account && p.account.id !== userDetails?.id)?.account
  }, [otherAccountFromParticipants, root_post, conversation?.descendants, userDetails?.id])

  const mutation = useMutation({
    mutationFn: async (vars) => {
      const status = await createComment(vars)
      // The sidecar has no other way to learn a reply landed in this thread —
      // record it so the other participant's unread badge picks it up.
      if (conversation_id) {
        await touchConversation({ token, id: conversation_id, author: userDetails?.id })
      }
      return status
    },
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: ['getComments', root_id, token] })
      queryClient.invalidateQueries({ queryKey: ['getMyConversations'] })
      setContent("")
      setPendingMedia([])
    },
  })

  // Mastodon resolves direct-message recipients from @mentions parsed out of
  // the status text, not from the parent post, so replies must re-mention
  // every other participant or the other side never sees them.
  const participants = React.useMemo(() => {
    const posts = [root_post, ...(conversation?.descendants ?? [])]
    const accts = new Map()
    posts.forEach((post) => {
      if (post?.account && post.account.id !== userDetails?.id) {
        accts.set(post.account.id, post.account.acct)
      }
    })
    return Array.from(accts.values())
  }, [conversation?.descendants, root_post, userDetails?.id])

  const pickImage = async (e) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    setUploadingMedia(true)
    try {
      const uploaded = await uploadMedia({ image: file, token })
      if (uploaded?.id) {
        setPendingMedia((prev) => [...prev, { id: uploaded.id, previewUrl: URL.createObjectURL(file) }])
      }
    } finally {
      setUploadingMedia(false)
    }
  }

  const removeMedia = (index) => {
    setPendingMedia((prev) => prev.filter((_, i) => i !== index))
  }

  // Every reply attaches to the root message (not the previous reply), so the
  // thread stays a flat, chronological list of descendants under one anchor
  // post instead of fragmenting into separate sub-threads.
  const send_reply = () => {
    if (!content.trim() && pendingMedia.length === 0) return
    const mentions = participants.map((acct) => '@' + acct).join(' ')
    mutation.mutate({
      body:{
        status: (mentions ? mentions + ' ' : '') + content,
        in_reply_to_id:root_id,
        visibility:"direct",
        ...(pendingMedia.length > 0 && { media_ids: pendingMedia.map(m => m.id) }),
      },
      token
    })
  }

  const canSend = (content.trim().length > 0 || pendingMedia.length > 0) && !mutation.isPending

  return (
    <Box sx={{
      maxWidth: 640,
      mx: 'auto',
      background: '#fff',
      borderRadius: 3,
      boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    }}>
      {/* ── Header ── */}
      <Box sx={{
        position: 'sticky',
        top: 0,
        zIndex: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        background: '#fff',
        borderBottom: '1px solid #ececf0',
        px: 2,
        py: 1.5,
      }}>
        <Avatar
          src={otherAccount?.avatar}
          onClick={() => otherAccount?.id && navigate('/profile/' + otherAccount.id)}
          sx={{ width: 42, height: 42, cursor: otherAccount?.id ? 'pointer' : 'default' }}
        />
        <Box
          sx={{ flex: 1, minWidth: 0, cursor: otherAccount?.id ? 'pointer' : 'default' }}
          onClick={() => otherAccount?.id && navigate('/profile/' + otherAccount.id)}
        >
          <Typography fontWeight={800} fontSize={15} noWrap sx={{ color: NAV }}>
            {otherAccount?.display_name || otherAccount?.username || 'Message'}
          </Typography>
          {otherAccount?.acct && (
            <Typography fontSize={12} sx={{ color: MUTED }} noWrap>@{otherAccount.acct}</Typography>
          )}
        </Box>
        <IconButton
          aria-label="more"
          id="long-button"
          aria-controls={open ? 'long-menu' : undefined}
          aria-expanded={open ? 'true' : undefined}
          aria-haspopup="true"
          onClick={handleClick}
        >
          <MoreVert sx={{ color: MUTED }} />
        </IconButton>
        <Menu
          id="long-menu"
          MenuListProps={{ 'aria-labelledby': 'long-button' }}
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
        >
          <MenuItem onClick={handleClose}>
            <DeleteConversation
              token={token}
              conversationId={conversation_id}
              statusIds={[root_post, ...(conversation?.descendants ?? [])].filter(s => s?.account?.id === userDetails?.id).map(s => s.id).filter(Boolean)}
            />
          </MenuItem>
        </Menu>
      </Box>

      {/* ── Messages ── */}
      <Box sx={{
        flex: 1,
        minHeight: 380,
        maxHeight: width > 900 ? 520 : '60vh',
        overflowY: 'auto',
        background: BG,
        px: 2,
        py: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
      }}>
        <MessageItem item={root_post} last_post={!conversation?.descendants?.length} />
        {[...(conversation?.descendants ?? [])].sort((a, b) => new Date(a.created_at) - new Date(b.created_at)).map((item, i, arr) => <MessageItem item={item} last_post={i === arr.length - 1} key={item.id ?? i} />)}
      </Box>

      {/* ── Input bar ── */}
      <Box sx={{
        borderTop: '1px solid #ececf0',
        background: '#fff',
        p: 1.5,
        position: 'sticky',
        bottom: width > 900 ? 0 : 50,
      }}>
        {pendingMedia.length > 0 && (
          <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
            {pendingMedia.map((m, i) => (
              <Box key={i} sx={{ position: 'relative' }}>
                <Box component="img" src={m.previewUrl} sx={{ width: 58, height: 58, borderRadius: 2, objectFit: 'cover', display: 'block' }} />
                <IconButton
                  size="small"
                  onClick={() => removeMedia(i)}
                  sx={{
                    position: 'absolute', top: -8, right: -8, width: 20, height: 20,
                    background: 'rgba(0,0,0,0.65)', color: '#fff',
                    '&:hover': { background: 'rgba(0,0,0,0.85)' },
                  }}
                >
                  <Close sx={{ fontSize: 12 }} />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
          <IconButton
            component="label"
            disabled={uploadingMedia}
            sx={{ color: MUTED, mb: 0.25 }}
          >
            {uploadingMedia ? <CircularProgress size={20} /> : <ImageOutlined />}
            <VisuallyHiddenInput type="file" accept="image/*" onChange={pickImage} />
          </IconButton>
          <TextField
            value={content}
            onChange={(e) => setContent(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send_reply() } }}
            multiline
            maxRows={4}
            fullWidth
            placeholder="Message…"
            variant="outlined"
            size="small"
            sx={{
              background: BG,
              '& .MuiOutlinedInput-root': { borderRadius: 4, '& fieldset': { border: 'none' } },
            }}
          />
          <IconButton
            onClick={send_reply}
            disabled={!canSend}
            sx={{
              background: canSend ? NAV : BG,
              border: canSend ? 'none' : `1px solid #DDE4EF`,
              color: canSend ? '#fff' : MUTED,
              width: 40, height: 40,
              '&:hover': { background: canSend ? '#16202f' : BG },
              '&.Mui-disabled': { color: MUTED },
            }}
          >
            {mutation.isPending ? <CircularProgress size={18} sx={{ color: canSend ? '#fff' : MUTED }} /> : <Send sx={{ fontSize: 18 }} />}
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
}
